import { useEffect, useState } from "react";
import { CheckSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND = {
  in_network: { label: "In network", cls: "ss-pill-green" },
  out_of_network: { label: "Out of network", cls: "ss-pill-amber" },
  available: { label: "Not selected", cls: "ss-pill-gray" },
};

// 2026-10-27 — "Select QB vendors", the way "Select QB jobs" works: the
// company's QuickBooks vendors, in or out of the network. Vendors already in
// the network are linked to their QuickBooks vendor and stay on; ticking any
// other makes it an out-of-network vendor (with its QuickBooks email); un-
// ticking turns it off again — its history stays.
export default function QboVendorSelect({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [checked, setChecked] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const openDialog = async () => {
    setOpen(true); setRows(null); setError(null); setSearch("");
    try {
      const { data } = await api.get("/contractor/integrations/qbo/select/vendors");
      setRows(data);
      setChecked(data.filter((r) => r.selected).map((r) => r.qbo_id));
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't read your QuickBooks vendors");
    }
  };

  useEffect(() => { if (!open) setRows(null); }, [open]);

  const toggle = (id) => setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/contractor/integrations/qbo/select/vendors", { qbo_ids: checked });
      toast.success(`${data.selected} vendor(s) selected — ${data.created + data.reactivated} out-of-network added, ${data.linked} linked to your network, ${data.deactivated} turned off`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save selection");
    } finally {
      setSaving(false);
    }
  };

  const shown = (rows || []).filter((r) => !search || `${r.name} ${r.email || ""}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <button onClick={openDialog} data-testid="select-qbo-vendors-button"
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
        <CheckSquare size={14} /> Select QB vendors
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="select-vendors-modal">
          <DialogHeader><DialogTitle>Select QB vendors</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500">
            Your QuickBooks vendors, in or out of your network. Tick the ones you buy from — vendors not yet on ScapeSource appear under Out-of-Network, where you can invite them.
          </p>
          {error && <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3.5 py-3">{error}</div>}
          {!rows && !error && <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Reading your QuickBooks vendors…</div>}
          {rows && (
            <>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendors…" data-testid="select-vendor-search" className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
              <div className="max-h-72 overflow-y-auto ss-scrollbar border border-slate-100 rounded-lg">
                {shown.map((r) => (
                  <label key={r.qbo_id} className={`flex items-center gap-2.5 text-[13px] px-3 py-2 border-b border-slate-50 last:border-0 ${r.locked ? "bg-slate-50/60" : "hover:bg-slate-50"}`} data-testid={`select-vendor-row-${r.qbo_id}`}>
                    <input type="checkbox" checked={checked.includes(r.qbo_id)} disabled={r.locked} onChange={() => toggle(r.qbo_id)} data-testid={`select-vendor-checkbox-${r.qbo_id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{r.name}</div>
                      <div className="text-[11.5px] text-slate-400 truncate">{r.email || "No email in QuickBooks"}</div>
                    </div>
                    <span className={`ss-pill ${KIND[r.kind].cls} shrink-0`}>{KIND[r.kind].label}</span>
                  </label>
                ))}
                {rows.length === 0 && <div className="text-slate-400 text-[12.5px] text-center py-6">No vendors pulled from QuickBooks yet — run Sync Now under Settings → Integrations first.</div>}
                {rows.length > 0 && shown.length === 0 && <div className="text-slate-400 text-[12.5px] text-center py-6">No vendor matches.</div>}
              </div>
              <button disabled={saving} onClick={save} data-testid="save-vendor-selection-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
                {saving ? "Saving…" : "Save Selection"}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
